import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Printer, Download, User, Phone, Envelope, Plus, FloppyDisk, PaperPlaneTilt } from "@phosphor-icons/react";

/**
 * Shared shell for custom module pages: customer picker (with quick-create) + action bar (print/download/email).
 * The parent supplies:
 *  - title, subtitle, icon
 *  - buildPayload(customerId): object POSTed to `endpoint`
 *  - endpoint, filenamePrefix
 *  - emailSubject(customer): string, emailBody(customer): string
 *  - children: form UI for module-specific inputs
 *  - customerRequired (bool, default true)
 *  - onCustomerChange(customer) hook (optional)
 *  - validate(customer): string|null   pre-flight custom validator
 */
export default function ModuleShell({
  title, subtitle, icon: Icon,
  endpoint, filenamePrefix,
  buildPayload,
  emailSubject, emailBody,
  children,
  customerRequired = true,
  onCustomerChange,
  validate,
}) {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [newCustomerDialog, setNewCustomerDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadCustomers = async () => {
    setLoading(true);
    const { data } = await api.get("/customers");
    setCustomers(data);
    setLoading(false);
    return data;
  };
  useEffect(() => { loadCustomers(); }, []);

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === customerId) || null,
    [customers, customerId]
  );

  useEffect(() => { onCustomerChange && onCustomerChange(selectedCustomer); /* eslint-disable-next-line */ }, [selectedCustomer]);

  const filteredCustomers = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return customers;
    return customers.filter((c) =>
      `${c.surname} ${c.name} ${c.phone} ${c.email || ""} ${c.ragione_sociale || ""}`.toLowerCase().includes(query)
    );
  }, [customers, q]);

  const doGenerate = async () => {
    if (customerRequired && !customerId) { toast.error("Seleziona un cliente"); return null; }
    if (validate) {
      const err = validate(selectedCustomer);
      if (err) { toast.error(err); return null; }
    }
    setBusy(true);
    try {
      const payload = buildPayload(customerId);
      const { data } = await api.post(endpoint, payload, { responseType: "blob" });
      return new Blob([data], { type: "application/pdf" });
    } catch (e) {
      let msg = "Errore generazione PDF";
      try {
        if (e.response?.data instanceof Blob) {
          const t = await e.response.data.text();
          msg = formatApiErrorDetail(JSON.parse(t).detail) || msg;
        } else {
          msg = formatApiErrorDetail(e.response?.data?.detail) || msg;
        }
      } catch { /* ignore */ }
      toast.error(msg);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const filename = () => `${filenamePrefix}-${(selectedCustomer?.surname || "cliente").toLowerCase()}.pdf`;

  const download = async () => {
    const blob = await doGenerate();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename();
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("PDF scaricato");
  };

  const print = async () => {
    const blob = await doGenerate();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const w = window.open(url);
    if (w) w.addEventListener("load", () => { try { w.focus(); w.print(); } catch {} });
    else toast.error("Popup bloccato: consenti la finestra per stampare");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const sendByEmail = async () => {
    if (!selectedCustomer?.email) { toast.error("Il cliente non ha un'email registrata"); return; }
    const blob = await doGenerate();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename();
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    const subject = encodeURIComponent(emailSubject ? emailSubject(selectedCustomer) : `${title} - Diotech`);
    const body = encodeURIComponent(
      emailBody ? emailBody(selectedCustomer) : `Gentile ${selectedCustomer.surname} ${selectedCustomer.name},\n\nin allegato trova il documento richiesto.`
    );
    setTimeout(() => {
      window.location.href = `mailto:${selectedCustomer.email}?subject=${subject}&body=${body}`;
      toast.info("PDF scaricato. Trascinalo nell'email che si sta aprendo per allegarlo.", { duration: 6000 });
    }, 200);
  };

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto">
      <Link to="/moduli" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Elenco moduli
      </Link>
      <header className="mb-6">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">// MODULO</div>
        <h1 className="text-3xl font-display font-bold tracking-tight flex items-center gap-3">
          {Icon && <Icon size={28} weight="duotone" className="text-primary" />}
          {title}
        </h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </header>

      <Card className="p-6 border-border/70 rounded-lg space-y-5">
        {/* Customer picker */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="font-mono text-[10px] uppercase tracking-widest">Cliente {!customerRequired && <span className="text-muted-foreground/70 normal-case">(opzionale)</span>}</Label>
            <Button type="button" variant="ghost" size="sm" onClick={() => setNewCustomerDialog(true)} className="rounded-full gap-1 text-xs" data-testid="module-new-customer-btn">
              <Plus size={13} /> Nuovo cliente
            </Button>
          </div>
          <Select value={customerId} onValueChange={setCustomerId}>
            <SelectTrigger data-testid="module-customer-select">
              <SelectValue placeholder={loading ? "Caricamento…" : "Seleziona un cliente registrato"} />
            </SelectTrigger>
            <SelectContent className="max-h-96">
              <div className="p-2 sticky top-0 bg-popover z-10 border-b border-border/50">
                <Input placeholder="Cerca cliente…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8" onKeyDown={(e) => e.stopPropagation()} />
              </div>
              {filteredCustomers.length === 0 && <div className="p-4 text-center text-xs text-muted-foreground">Nessun cliente</div>}
              {filteredCustomers.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.surname} {c.name} · {c.phone}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedCustomer && (
            <div className="mt-3 p-3 rounded-md border border-border/60 bg-secondary/40 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <User size={14} weight="duotone" className="text-primary" />
                <div className="font-medium">{selectedCustomer.surname} {selectedCustomer.name}</div>
                {selectedCustomer.ragione_sociale && <Badge className="bg-secondary text-foreground border-border hover:bg-secondary font-mono text-[9px] uppercase">{selectedCustomer.ragione_sociale}</Badge>}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground font-mono">
                <span className="inline-flex items-center gap-1"><Phone size={11} /> {selectedCustomer.phone}</span>
                {selectedCustomer.email && <span className="inline-flex items-center gap-1"><Envelope size={11} /> {selectedCustomer.email}</span>}
                {selectedCustomer.piva && <span>P.IVA: {selectedCustomer.piva}</span>}
              </div>
            </div>
          )}
        </div>

        {/* Module content (form fields) */}
        <div className="border-t border-border/50 pt-5">
          {children}
        </div>

        {/* Actions */}
        <div className="border-t border-border/50 pt-4 flex flex-col sm:flex-row gap-2 justify-end flex-wrap">
          <Button variant="outline" onClick={sendByEmail} disabled={busy || !selectedCustomer?.email} className="rounded-full gap-2" title={!selectedCustomer?.email ? "Il cliente non ha email" : ""}>
            <PaperPlaneTilt size={16} /> Invia via email
          </Button>
          <Button variant="outline" onClick={print} disabled={busy} className="rounded-full gap-2" data-testid="module-print-btn">
            <Printer size={16} /> Stampa
          </Button>
          <Button onClick={download} disabled={busy} className="rounded-full gap-2" data-testid="module-download-btn">
            <Download size={16} /> {busy ? "Attendi…" : "Scarica PDF"}
          </Button>
        </div>
      </Card>

      <QuickCustomerDialog open={newCustomerDialog} onOpenChange={setNewCustomerDialog}
        onCreated={async (id) => { await loadCustomers(); setCustomerId(id); setNewCustomerDialog(false); toast.success("Cliente creato e selezionato"); }} />
    </div>
  );
}

function QuickCustomerDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ surname: "", name: "", phone: "", email: "", ragione_sociale: "", piva: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!open) setForm({ surname: "", name: "", phone: "", email: "", ragione_sociale: "", piva: "" }); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.surname.trim() || !form.phone.trim()) return toast.error("Cognome, nome e telefono obbligatori");
    setSaving(true);
    try {
      const { data } = await api.post("/customers", {
        name: form.name.trim(), surname: form.surname.trim(), phone: form.phone.trim(),
        email: form.email.trim() || null, ragione_sociale: form.ragione_sociale.trim() || null, piva: form.piva.trim() || null,
      });
      onCreated(data.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nuovo cliente rapido</DialogTitle>
          <DialogDescription>Dati minimi. Potrai completarli dopo dalla scheda cliente.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="font-mono text-[10px] uppercase tracking-widest">Cognome *</Label><Input required value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="font-mono text-[10px] uppercase tracking-widest">Nome *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div><Label className="font-mono text-[10px] uppercase tracking-widest">Telefono *</Label><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 font-mono" /></div>
          <div><Label className="font-mono text-[10px] uppercase tracking-widest">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="font-mono text-[10px] uppercase tracking-widest">Ragione sociale</Label><Input value={form.ragione_sociale} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="font-mono text-[10px] uppercase tracking-widest">P.IVA</Label><Input value={form.piva} onChange={(e) => setForm({ ...form, piva: e.target.value })} className="mt-1.5 font-mono" /></div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={saving} className="rounded-full gap-2"><FloppyDisk size={16} /> {saving ? "Salvo…" : "Crea"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
